<template>
  <VSkeletonLoader
    grid-list-lg
    fluid
    type="table-heading,card"
    :loading="activity.isFirstLoad"
    :class="{
      'white pa-2': activity.isFirstLoad,
    }"
  >
    <VRow class="evaluation-save">
      <SaveHeader
        alt-back-icon
        :owner="this"
        :mode="mode"
      >
        <template #actions-center>
          <VTooltip
            top
            content-class="required-tooltip"
            :disabled="Boolean(form.evaluation_form.title)"
          >
            <template #activator="{ on }">
              <div v-on="on">
                <VBtn
                  height="40px"
                  color="#60be39"
                  class="mr-3 ml-1 px-6"
                  style="box-shadow: 0 1.5px 3px 0 rgba(250, 36, 111, 0.25); border-radius: 10px;"
                  :disabled="isSaveDisabled"
                  :loading="activity.isFormLoading"
                  @click="onSave"
                >
                  <span class="white--text">{{ `Save Form` }}</span>
                </VBtn>
              </div>
            </template>
            {{ `Enter a Title to Save the Evaluation form` }}
          </VTooltip>
        </template>
      </SaveHeader>
      <VCol style="flex: 1 1 auto">
        <VCard color="transparent">
          <VCardText v-if="form.hasOwnProperty('evaluation_form')">
            <VRow v-if="useExisting">
              <VCol>
                <VAutocomplete
                  v-model="form.evaluation_form"
                  dense
                  outlined
                  item-text="title"
                  item-value="id"
                  label="Select existing form"
                  :items="options.deliverables"
                  :loading="!options.hasOwnProperty('deliverables')"
                  :disabled="!options.hasOwnProperty('deliverables')"
                />
              </VCol>
            </VRow>
            <VRow
              v-else
              style="width: 100%;"
            >
              <VCol
                lg="2"
                md="3"
                class="pl-0"
              >
                <VList
                  color="transparent"
                  class="questions-list"
                >
                  <VListItem>
                    <div
                      class="d-flex"
                      style="cursor: pointer;"
                      @click="addNewPage"
                    >
                      <VBtn
                        color="#252526"
                        height="40px"
                        min-width="auto"
                        class="px-2 mr-3"
                        style="border-radius: 10px;"
                      >
                        <img
                          height="24px"
                          src="../../../images/forms/add-icon@2x.png"
                        >
                      </VBtn>
                      <VListItemTitle>
                        <span style="color: #252526; font-weight: 500;">
                          Add Page
                        </span>
                      </VListItemTitle>
                    </div>
                  </VListItem>
                  <Draggable
                    v-if="pages.length > 0"
                    handle=".dynamic-page-handle"
                    :no-transition-on-drag="true"
                    :animation="200"
                    :value="pages"
                    @change="event => $refs.dynamicInputs.onSectionMoved(event)"
                  >
                    <transition-group
                      name="dynamic-pages"
                      type="transition"
                    >
                      <VListItem
                        v-for="(page, key) in pages"
                        class="pt-2"
                        :key="`dynamic-page-${key}`"
                      >
                        <VListItemTitle 
                          class="d-flex align-center"
                          :style="{
                            cursor: page !== defaultPageTitle ? 'pointer' : 'default',
                          }"
                          @click="defaultPageTitle = page"
                        >
                          <VBtn
                            height="40px"
                            width="40px"
                            min-width="40px"
                            class="px-2 mr-3 no-shadow"
                            :color="page === defaultPageTitle ? '#ffffff' : '#f4f4f9'"
                            :style="{
                              border: '1px solid #252526',
                              'border-radius': '10px',
                              cursor: page !== defaultPageTitle ? 'pointer' : 'default',
                            }"
                          >
                            <span style="font-size: 16px; color: #252526">{{ key + 1 }}</span>
                          </VBtn>
                          <span
                            class="text-truncate"
                            :style="{
                              color: '#252526',
                              'font-weight': 500,
                              'max-width': $vuetify.breakpoint.lgAndUp ? '250px' : '130px',
                            }"
                          >
                            {{ page }}
                          </span>
                          <VSpacer />
                          <img
                            v-if="pages.length > 1"
                            height="24px"
                            class="dynamic-page-handle pl-2"
                            style="cursor: grabbing;"
                            src="../../../images/forms/drag-icon@2x.png"
                          >
                        </VListItemTitle>
                      </VListItem>
                    </transition-group>
                  </Draggable>
                </VList>
              </VCol>
              <VCol style="height: calc(100vh - 156px); overflow: hidden auto;">
                <VCard
                  v-if="form.hasOwnProperty('evaluation_form')"
                  class="py-2"
                  style="box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.2);"
                >
                  <VCardTitle
                    class="pa-4"
                    style="height: 100% !important; border: none !important;"
                  >
                    <TextField
                      v-model="form.evaluation_form.title"
                      label="Title"
                      hide-details
                      placeholder="Enter a title for the evaluation form..."
                      :error="$v.form.evaluation_form.title.$error"
                      @input="$v.form.evaluation_form.title.$touch()"
                      @blur="$v.$touch()"
                    />
                  </VCardTitle>
                  <VCardText class="pa-0">
                    <VSelect
                      v-model="form.evaluation_form.allow_multiple_submissions"
                      dense
                      outlined
                      hide-details
                      class="px-3 ma-2"
                      label="Allow multiple submissions?"
                      :items="[
                        {
                          value: true,
                          text: 'Users can submit multiple times',
                        },
                        {
                          value: false,
                          text: 'Users can submit only once',
                        },
                      ]"
                    />
                    <VRow v-if="hasMetadata">
                      <VCol>
                        <DynamicInputs
                          v-model="form.evaluation_form.metadata"
                          fields-only
                          new-fields-view
                          enable-default-values
                          enable-extra-options
                          hide-last-divider
                          ref="dynamicInputsMain"
                          container-class="dynamic-input-container px-4 white"
                          :card="false"
                          :readonly="true"
                          :enable-files="true"
                          :values-from-user="true"
                          :can-add-more-inputs="false"
                          :show-delete-btn-text="false"
                          :default-section-title="defaultPageTitle"
                          :input-name="featureName('Evaluation Form')"
                          :sections="form.evaluation_form.metadata_sections"
                          :default-value-options="options.default_metadata_value_options"
                          @removeSection="removePage"
                          @new:input="(value, index) => onNewMetadata(value, index, 'form.evaluation_form')"
                          @update:input="(value, index) => onUpdateMetadata(value, index, 'form.evaluation_form')"
                          @update:inputs="(value) => onUpdateMultipleMetadata(value, 'form.evaluation_form')"
                          @remove:input="(index) => onRemoveMetadata(index, 'form.evaluation_form')"
                          @remove:inputs="(value) => onRemoveMultipleMetadata(value, 'form.evaluation_form')"
                          @input:sections="(sections) => onSetMetadataSections(sections, null, 'form.evaluation_form')"
                          @update:sections="(key, input) => onUpdateMetadataSections(key, input, null, 'form.evaluation_form')"
                          @updateDefaultSectionTitle="title => defaultPageTitle = title"
                        />
                      </VCol>
                    </VRow>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="auto">
                <DynamicInputs
                  v-if="form.hasOwnProperty('evaluation_form')"
                  v-model="form.evaluation_form.metadata"
                  hide-fields
                  list-view
                  enable-extra-options
                  enable-default-values
                  ref="dynamicInputs"
                  questions-list-style="overflow: hidden auto; height: calc(100vh - 180px);"
                  :questions-list-class="$vuetify.breakpoint.md ? 'ml-n4' : 'ml-2'"
                  :readonly="true"
                  :values-from-user="true"
                  :default-section-title="defaultPageTitle"
                  :sections="form.evaluation_form.metadata_sections"
                  :default-value-options="options.default_metadata_value_options"
                  @input:sections="(sections) => onSetMetadataSections(sections, null, 'form.evaluation_form')"
                  @removeFile="onRemoveFile"
                  @addFile="onAddFile"
                />
                <Confirm ref="confirmDelete" />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </VSkeletonLoader>
</template>
<script>
import { mapActions } from "vuex";
import { required, minLength } from "vuelidate/lib/validators";
import Draggable from "vuedraggable";
import FormMixin from "@/mixins/Form";
import MetadataMixin from "@/mixins/Metadata";
import DeletesItems from "@/mixins/DeletesItems";
import DynamicInputs from "@/components/Elements/Forms/DynamicInputs";
import SaveHeader from "@/components/Elements/Navigation/SaveHeader";

export default {
  name: "EvaluationSave",
  props: {
    value: {
      type: [Object, Array],
      default: null,
    },
    content: {
      type: Object,
      default: () => {},
    },
    contentType: {
      type: String,
      default: "pipeline",
    },
    activeUseExisting: {
      type: Boolean,
      default: false,
    },
    canGetEvaluation: {
      type: Boolean,
      default: true,
    },
  },
  mixins: [
    FormMixin,
    MetadataMixin,
    DeletesItems,
  ],
  components: {
    DynamicInputs,
    Draggable,
    SaveHeader,
  },
  data() {
    return {
      useExisting: false,
      modelType: "PipelineEvaluationForm",
      hasFiles: true,
      redirectOnSave: true,
      defaultPageTitle: "Page Title 1",
      cachedForm: null,
      dialogs: {
        preview: false,
      },
      options: {
        default_metadata_value_options: {},
      },
      defaultForm: {
        evaluation_form: {
          title: null,
          allow_multiple_submissions: true,
          metadata: [],
          metadata_sections: [],
        },
      },
    };
  },
  validations() {
    if (this.useExisting) {
      return {};
    } else {
      return {
        form: {
          evaluation_form: {
            title: {
              required,
              minLength: minLength(4),
            },
          },
        },
      };
    }
  },
  watch: {
    activeUseExisting: {
      handler: function (val) {
        this.useExisting = val;
      },
      immediate: true,
    },
    canGetEvaluation: {
      handler: function (val) {
        this.canGetModel = val;
      },
      immediate: true,
    },
    "options.default_metadata_value_options": function (updatedOptions) {
      this.$emit("defaultOptions", updatedOptions);
    },
    "activity.isFormLoading": {
      handler: function () {
        if(this.form.hasOwnProperty("evaluation_form") && !this.form.evaluation_form.metadata.length) {
          this.addNewPage();
        }
      },
      deep: true,
    },
  },
  computed: {
    hasMetadata() {
      return this.form.hasOwnProperty("evaluation_form") && this.form.evaluation_form.hasOwnProperty("metadata") && this.form.evaluation_form.metadata.length > 0;
    },
    pages() {
      if(this.hasMetadata) {
        let inputs =  this.form.evaluation_form.metadata.groupBy((input) => input.section);
        return Object.keys(inputs || {}).unique();
      }
      return [];
    },
    isSaveDisabled() {
      return !this.isFormReady || this.isFormInputsDisabled || this.form.isLoading;
    },
  },
  mounted() {
    if(!this.canGetEvaluation && !this.useExisting) {
      const getter =
        this.contentType == "pipeline" ? this.doGetPipeline : this.doGetPipelineStage;
      getter({
        id: this.id,
        pipeline_id: this.contentType == "pipeline" ? this.content.id : this.content.pipeline_id,
        _fields: ["id", "evaluation_form"],
        _with: ["evaluationForm"],
      }).then((result) => {
        console.log("------------", result, this.form)
        if (!result.evaluation_form) {
          this.$set(result, "evaluation_form", this.form.evaluation_form);
        } else {
          this.form.evaluation_form.id = result.evaluation_form.id;
        }
        this.cachedForm = result;
      });
    }
  },
  methods: {
    ...mapActions({
      doGetFormConfig: "pipeline/getFormConfig",
      doSavePipeline: "pipeline/save",
      doGetPipeline: "pipeline/getOne",
      doSavePipelineStage: "pipelineStage/save",
      doGetPipelineStage: "pipelineStage/getOne",
    }),
    onGetFormConfig() {
      return this.doGetFormConfig();
    },
    onSave() {
      let tempMetadata = [];
      this.form.evaluation_form =
        this.form.evaluation_form || this.defaultForm.evaluation_form;
      this.form.id = this.id;
      if (this.contentType != "pipeline") {
        this.form.pipeline_id = this.content.pipeline_id;
      }
      if(!this.canGetEvaluation && !this.useExisting && this.cachedForm && this.form.evaluation_form.hasOwnProperty("id")) {
        tempMetadata = this.cachedForm.evaluation_form.metadata.map((metadata) => {
          metadata.deleted = true;
          return metadata;
        });
      }
      const data = this.prepareFormModel(
        { 
          ...this.form,
          evaluation_form: this.useExisting ? this.form.evaluation_form : {
            ...this.form.evaluation_form,
            metadata: [
              ...this.form.evaluation_form.metadata,
              ...tempMetadata,
            ],
          },
        },
        {
          saveOnly: ["id", "pipeline_id", "evaluation_form"],
        }
      );
      this.$log.debug("Save Evaluation: Prepared Form Data", this.form.evaluation_form, tempMetadata, data);
      const saver =
        this.contentType == "pipeline" ? this.doSavePipeline : this.doSavePipelineStage;
      return this.saveFormModel(saver, null, data, null, false).then((result) => {
        if (result) {
          this.cachedForm = result;
          this.form = result;
          this.$bus.$emit("notificationMessage", "Evaluation Form saved successfully!");
          this.$refs.dynamicInputsMain.prepareValue(true);
          this.$emit("update:form", result.evaluation_form);
          return result;
        }
      });
    },
    onGet(id) {
      const getter =
        this.contentType == "pipeline" ? this.doGetPipeline : this.doGetPipelineStage;
      return getter({
        id: id,
        pipeline_id:
          this.contentType == "pipeline" ? this.content.id : this.content.pipeline_id,
        _fields: ["id", "evaluation_form"],
        _with: ["evaluationForm"],
      }).then((result) => {
        this.form = {
          id: this.form.id,
        };
        if (!result.evaluation_form) {
          this.form.evaluation_form = this.defaultForm.evaluation_form;
          this.$set(result, "evaluation_form", this.form.evaluation_form);
        }
        if(!this.canGetEvaluation) {
          this.$set(this.form, "evaluation_form", this.defaultForm.evaluation_form);
        }
        this.cachedForm = result;
        return result;
      });
    },
    addNewPage() {
      this.defaultPageTitle = `Page Title ${this.pages.length + 1}`;
      this.$nextTick(() => {
        this.$refs.dynamicInputs.onAddInput({
          text: "Short Text",
          value: "Text",
          icon: "mdi-text",
        });
      });
    },
    removePage() {
      if(this.pages.length > 0) {
        this.defaultPageTitle = this.form.metadata[0].section;
      }
    },
  },
};
</script>
<style lang="scss">
.evaluation-save {
  .save-header {
    padding: 20px 0px;
    margin-right: 12px;
  }
  .checkbox-label .v-label {
    font-size: 14px !important;
    color: #808080 !important;
    font-weight: 500;
  }
}
.form-tabs {
  flex: 0;
  .v-tabs-bar__content {
    height: 40px !important;
    margin-top: 4px !important;
  }
}
</style>
